import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { TournamentSummary } from "../../lib/api";
import { ApiError } from "../../lib/api";
import { preparationApi } from "../../lib/preparationApi";

export function NewTournamentDialog({
  season,
  tournaments,
  onClose,
  onCreated,
}: {
  season: number;
  tournaments: TournamentSummary[];
  onClose: () => void;
  onCreated: (tournamentId: string, season: number) => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [location, setLocation] = useState("");
  const [country, setCountry] = useState("");
  const [copyFromTournamentId, setCopyFromTournamentId] = useState("");
  const [copyStaff, setCopyStaff] = useState(false);

  const create = useMutation({
    mutationFn: () => preparationApi.createTournament({
      name,
      season,
      startDate,
      endDate,
      location,
      country,
      copyFromTournamentId,
      copyStaff: Boolean(copyFromTournamentId && copyStaff),
    }),
    onSuccess: async (data) => {
      queryClient.setQueryData(["tournament-setup", data.tournament.id], data);
      await queryClient.invalidateQueries({ queryKey: ["tournaments", season] });
      onCreated(data.tournament.id, data.tournament.season);
      onClose();
    },
  });

  const error = create.error instanceof ApiError ? create.error.message : create.error instanceof Error ? create.error.message : "";

  return (
    <div className="prep-drawer-layer" role="presentation">
      <button className="prep-drawer-backdrop" type="button" aria-label="Zamknij" onClick={onClose} />
      <aside className="prep-drawer tournament-details-drawer" aria-label="Nowy turniej">
        <header>
          <div><span className="eyebrow">NEW TOURNAMENT</span><h3>Nowy turniej {season}</h3><p>Startujesz z gotową bazową flotą jak w develop v1 albo kopiujesz ustawienia poprzedniego eventu.</p></div>
          <button type="button" className="prep-drawer-close" onClick={onClose}>×</button>
        </header>
        <section className="prep-editor-section">
          <span className="prep-editor-label">PODSTAWOWE DANE</span>
          <label className="prep-field"><span>Nazwa</span><input autoFocus value={name} onChange={(event) => setName(event.target.value)} placeholder="np. British Masters" /></label>
          <div className="tournament-date-grid">
            <label className="prep-field"><span>R1 / start</span><input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} /></label>
            <label className="prep-field"><span>R4 / koniec</span><input type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} /></label>
          </div>
          <label className="prep-field"><span>Lokalizacja</span><input value={location} onChange={(event) => setLocation(event.target.value)} /></label>
          <label className="prep-field"><span>Kraj</span><input value={country} onChange={(event) => setCountry(event.target.value)} /></label>
        </section>
        <section className="prep-editor-section">
          <span className="prep-editor-label">STARTOWA LOGISTYKA</span>
          <label className="prep-field"><span>Kopiuj ustawienia z turnieju</span><select value={copyFromTournamentId} onChange={(event) => { setCopyFromTournamentId(event.target.value); if (!event.target.value) setCopyStaff(false); }}><option value="">Nie kopiuj — Hotel + 5×9 i 1×6 miejsc</option>{tournaments.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
          {copyFromTournamentId ? <label className="check-line"><input type="checkbox" checked={copyStaff} onChange={(event) => setCopyStaff(event.target.checked)} /> Skopiuj również obsadę turnieju</label> : null}
          <p className="hint">Kopiowanie nie przenosi rund ani wyników. Tworzony jest nowy event z własnymi revisionami.</p>
        </section>
        {error ? <div className="prep-editor-error">{error}</div> : null}
        <footer><button type="button" className="secondary-button" onClick={onClose}>Anuluj</button><button type="button" className="primary-button" disabled={!name.trim() || create.isPending} onClick={() => create.mutate()}>{create.isPending ? "Tworzę…" : "+ Utwórz turniej"}</button></footer>
      </aside>
    </div>
  );
}
